import { useMutation, useQuery } from '@apollo/client';
import type { RelatedEntity, User } from '@interfaces/user';
import { getWorkflowCompletionHeaders } from '@ovComponents/4-module/workflowCompletion/workflowCompletionHeaders';
import {
  FETCH_USER_RELATED_ENTITIES,
  UPDATE_USER_RELATED_ENTITIES,
} from '@pages/ClientDetail/relatedEntities.query';
import { useSnackbar } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../../../../hooks/usePermission';
import { RelatedEntitiesVisual } from './relatedEntities.visual';

export const RelatedEntities = ({
  options,
  workflowCompletion,
  userId,
  onNext,
  stepLoading,
}: {
  options: any;
  workflowCompletion?: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
}) => {
  const { t } = useTranslation('workflowCompletions');
  const { enqueueSnackbar } = useSnackbar();
  const canReadClientLowRiskPii = usePermission('read:client_low_risk_pii');
  const [user, setUser] = useState<User>();

  const { loading: fetchUserLoading } = useQuery(
    FETCH_USER_RELATED_ENTITIES({ canReadClientLowRiskPii }),
    {
      variables: { userId },
      fetchPolicy: 'no-cache',
      onCompleted: (data) => setUser(data.fetchUser.user),
    },
  );
  const [
    updateUserRelatedEntities,
    { loading: updateUserRelatedEntitiesLoading },
  ] = useMutation(UPDATE_USER_RELATED_ENTITIES, {
    context: {
      headers: getWorkflowCompletionHeaders(workflowCompletion),
    },
    refetchQueries: [FETCH_USER_RELATED_ENTITIES({ canReadClientLowRiskPii })],
  });

  const handleDraftSubmission = async (draftRelatedEntity: RelatedEntity) => {
    const existingRelatedEntitiesInputs =
      user?.relatedEntities?.map((relEnt) => ({
        entityId: relEnt.entity.id,
        relation: relEnt.relation,
      })) ?? [];
    if (draftRelatedEntity) {
      await updateUserRelatedEntities({
        variables: {
          userId,
          relatedEntities: [
            ...existingRelatedEntitiesInputs,
            {
              entityId: draftRelatedEntity.entity.id,
              relation: draftRelatedEntity.relation,
            },
          ],
        },
        onCompleted: () => {
          enqueueSnackbar(
            t('workflowCompletions:relatedEntities.newRelatedEntitySuccess'),
            { variant: 'success' },
          );
        },
      });
    } else {
      await updateUserRelatedEntities({
        variables: {
          userId,
          relatedEntities: [...existingRelatedEntitiesInputs],
        },
        onCompleted: () => {
          enqueueSnackbar(
            t('workflowCompletions:relatedEntities.newRelatedEntitySuccess'),
            { variant: 'success' },
          );
        },
      });
    }
  };

  const loading =
    stepLoading || fetchUserLoading || updateUserRelatedEntitiesLoading;

  return (
    <RelatedEntitiesVisual
      user={user}
      options={options}
      loading={loading}
      onContinue={onNext}
      onDraftSubmission={handleDraftSubmission}
    />
  );
};
